/**
 * Calendar event-description HTML sanitizer.
 *
 * Provider calendar descriptions are UNTRUSTED third-party HTML. Google, Outlook
 * and Zoom all write rich descriptions — the meeting-details block is typically a
 * `<table>` of dial-in numbers, a `<ul>` of agenda items, 18 `<br>`s and an
 * anchor to the meeting link — and the organiser can be anyone who sent you an
 * invite. So this is the email threat model, not the chat one.
 *
 * These descriptions were rendered as TEXT, so every tag showed literally and a
 * one-paragraph agenda arrived as a wall of `<table><tbody><tr><th>` markup.
 * Reported as "the text is garbled and doesn't fit".
 *
 * Unlike `sanitize-email-html.ts` the result is injected INLINE into the app
 * DOM, not into a sandboxed iframe, because a description is a short block in a
 * dialog rather than a full document. That single difference drives the whole
 * allow-list, and it is why this is a separate module rather than a call into
 * the email one:
 *
 *   - NO `style` and NO `class`. In an iframe the email sanitizer can afford
 *     them because the frame isolates CSS; injected inline, attacker CSS can
 *     restyle or overlay the host app (clickjacking a real button under an
 *     invisible one). There is no iframe here, so there is no CSS.
 *   - NO `<img>`. A remote image in an invite from an unknown organiser is a
 *     tracking pixel that reports when you opened the event — the same
 *     deanonymisation the email module blocks by default. Text and links are
 *     what a description is for.
 *   - Structural tags ARE kept (table/list/anchor). Stripping to plain text
 *     would lose the dial-in table and, worse, the meeting link — usually the
 *     single most important thing in the description.
 *
 * Deliberately registers NO DOMPurify hooks. `sanitize-chat-html.ts` adds a
 * GLOBAL `afterSanitizeAttributes` hook to the shared singleton, so its policy
 * already runs over every consumer's output — but only if that module happens
 * to have been imported, which makes it import-order-dependent and not
 * something to rely on. Link hardening therefore happens in an explicit
 * post-sanitize pass below, the same way the email module does it.
 */

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p',
  'br',
  'hr',
  'div',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  'code',
  'pre',
  'blockquote',
  'ul',
  'ol',
  'li',
  'a',
  // Meeting-details blocks are overwhelmingly tables (dial-in numbers, room
  // lists). Keeping the structure is the difference between a readable block
  // and a run-on line.
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  // Some organisers head their agenda. Rendered at body size by the caller so a
  // description cannot shout over the dialog's own headings.
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
];

/** No `style`, no `class`, no `src` — see the module note. */
const ALLOWED_ATTR = ['href', 'target', 'rel'];

export function sanitizeCalendarHtml(dirty: string): string {
  const root = DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // The blanket data-* allowance would otherwise survive; nothing here needs
    // one.
    ALLOW_DATA_ATTR: false,
    // NOTE: do NOT add `USE_PROFILES`. DOMPurify treats it as authoritative and
    // IGNORES ALLOWED_TAGS / ALLOWED_ATTR when set, silently widening the list
    // (it would readmit <img>). The explicit lists above are the contract.
    // DOMPurify's defaults already block javascript: and data: URIs in href.
    RETURN_DOM: true,
  }) as unknown as HTMLElement;

  // Explicit post-sanitize pass — the email module's pattern, for the reason
  // given above.
  for (const el of Array.from(root.querySelectorAll('*'))) {
    const tag = el.tagName.toLowerCase();
    if (tag === 'a') {
      // Someone else's meeting link always opens away from the app, and always
      // without handing over the opener (reverse tabnabbing).
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
      continue;
    }
    // ALLOWED_ATTR is a single global list and cannot express "href only on
    // <a>", so scope it here.
    el.removeAttribute('href');
    el.removeAttribute('target');
    el.removeAttribute('rel');
  }

  return root.innerHTML;
}

/**
 * True when the description carries markup worth rendering as HTML.
 *
 * Most descriptions are plain text, and those must keep going through the
 * existing `whitespace-pre-wrap` path: routing them through the sanitizer would
 * collapse the provider's line breaks, since newlines are not `<br>`.
 */
export function looksLikeHtml(value: string): boolean {
  return HTML_TAG_PATTERN.test(value);
}

/**
 * Derived from ALLOWED_TAGS so the detector can never fall behind the
 * allow-list. Hand-written, it listed only a dozen tags and missed `pre`,
 * `code`, `blockquote`, `hr`, `thead`, `tbody`, `tr`, `th` and `td` — all of
 * which the sanitizer accepts, so a description built from any of them alone
 * took the plain-text path and printed its own markup at the user. That is the
 * exact bug this module exists to fix, reached through a different door.
 */
const HTML_TAG_PATTERN = new RegExp(`<(?:${ALLOWED_TAGS.join('|')})\\b[^>]*>`, 'i');
